import React, { useEffect, useMemo, useRef, useState } from "react";

import { AnalysisFeedbackPayload } from "@risalabs_frontend_org/oasis-ui-kit";
import { useSelector } from "react-redux";
import {
  ChecklistQuestion,
  QuestionPatch,
  RadioQuestion,
  SubjectiveQuestion,
} from "../../components/questionnaire";
import {
  Answer,
  PharmaBranchedQuestionnaireDocument,
  QuestionForUI,
  UserFeedback,
} from "../../data-model/cmmMedicalNeccessityQuestionnaire";
import questionExample from "../../data-model/questionExmaple.json";
import { RootState } from "../../redux/store/store";
import {
  appendUserFeedbackToCmmWorkflow,
  applyPatchToCmmWorkflow,
  clearUserFeedbackFromCmmWorkflow,
  transformCmmWorkflowToQuestionnaireForUI,
} from "../../utils/transformCmmQuestionnaire";

/**
 * Builds a lookup of `question.name → question.answer` from the first
 * clinical question section of a workflow document. Questions whose `name`
 * is `null` are skipped (they have no stable identifier to key by). Intended
 * to be called once against the pristine doc so callers can reference the
 * original answers across the lifetime of the page.
 */
function buildOriginalAnswerMap(
  doc: PharmaBranchedQuestionnaireDocument,
): Map<string, Answer> {
  const map = new Map<string, Answer>();
  const questions =
    doc.cmm_data_latest?.clinical_question_sections?.[0]?.questions ?? [];
  for (const q of questions) {
    if (q.name != null) {
      map.set(q.name, q.answer);
    }
  }
  return map;
}

/**
 * Returns true when `value` carries a usable answer, i.e. it is not `null`,
 * not `undefined`, not an empty string after trimming, and not an empty
 * array. Used by {@link validateQuestionnaireAnswers} to enforce a single
 * notion of "answered" across the heterogeneous `Answer` shapes.
 */
function hasMeaningfulValue(value: Answer | string | undefined): boolean {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  return value.trim() !== "";
}

/**
 * Returns true when two answers are equivalent. Strings compare directly,
 * arrays compare order-insensitively (sets of values), mixed shapes are
 * considered unequal. `null` / `undefined` / empty string / empty array are
 * all treated as "no answer" and therefore equivalent to each other.
 */
function areAnswersEqual(
  a: Answer | string | undefined,
  b: Answer | string | undefined,
): boolean {
  const aEmpty = !hasMeaningfulValue(a);
  const bEmpty = !hasMeaningfulValue(b);
  if (aEmpty && bEmpty) return true;
  if (aEmpty !== bEmpty) return false;
  const aArr = Array.isArray(a);
  const bArr = Array.isArray(b);
  if (aArr !== bArr) return false;
  if (aArr && bArr) {
    const aSorted = [...(a as string[])].sort();
    const bSorted = [...(b as string[])].sort();
    return (
      aSorted.length === bSorted.length &&
      aSorted.every((v, i) => v === bSorted[i])
    );
  }
  return a === b;
}

/**
 * Walks the question flow from the first question, following each
 * question's `next_question_name`, and returns the chain of questions the
 * user can currently see. Cycles are guarded with a `seen` set so a
 * misconfigured flow can't loop forever.
 */
function getVisibleQuestionChain(questions: QuestionForUI[]): QuestionForUI[] {
  if (questions.length === 0) return [];
  const byName = new Map(questions.map((q) => [q.name, q]));
  const visible: QuestionForUI[] = [];
  const seen = new Set<string>();
  let current: QuestionForUI | undefined = questions[0];
  while (current && !seen.has(current.name)) {
    visible.push(current);
    seen.add(current.name);
    const next = current.next_question_name;
    current = next ? byName.get(next) : undefined;
  }
  return visible;
}

/**
 * Validates the questionnaire answers for the supplied questions.
 *
 * Callers are expected to pass the subset of questions that should be
 * validated (typically the visible chain produced by
 * {@link getVisibleQuestionChain}); this function does not walk the flow
 * itself — that responsibility lives with the caller so the same chain can
 * be reused for rendering, validation, and logging without recomputing.
 *
 * Rules (all must pass; returns `false` on the first violation):
 *   1. Every question's `answer` must be present — non-null/undefined, a
 *      non-blank string, or a non-empty array.
 *   2. When a question has `isTextAreaMandatory === true`, the
 *      `free_text_answer.answer` must also be present (non-blank).
 *   3. If a question's current `answer` differs from the original answer
 *      recorded in `originalAnswersByName` (keyed by `question.name`), the
 *      `answerChangedReason` must be present (non-blank).
 *
 * @param questions             Questions to validate (e.g. the visible
 *                              chain).
 * @param originalAnswersByName Lookup of the original (pristine) answer per
 *                              `question.name`. Pass an empty map (or omit)
 *                              to skip rule #3.
 * @returns                     `true` when every rule holds for every
 *                              question; `false` otherwise.
 */
function validateQuestionnaireAnswers(
  questions: QuestionForUI[],
  originalAnswersByName: Map<string, Answer> = new Map(),
): boolean {
  for (const q of questions) {
    if (!hasMeaningfulValue(q.answer)) return false;

    if (q.isTextAreaMandatory && !hasMeaningfulValue(q.free_text_answer.answer))
      return false;

    if (originalAnswersByName.size > 0) {
      const originalAnswer = originalAnswersByName.get(q.name);
      if (
        !areAnswersEqual(originalAnswer, q.answer) &&
        !hasMeaningfulValue(q.answerChangedReason)
      ) {
        return false;
      }
    }
  }
  return true;
}

/**
 * Dummy page that loads the local example CMM workflow document, runs it
 * through the transformer, and renders each question with the appropriate
 * questionnaire wrapper. Useful for visually verifying the renderer pipeline
 * end-to-end.
 */
const TestQuestionnaire: React.FC = () => {
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const [workflowDocument, setWorkflowDocument] =
    useState<PharmaBranchedQuestionnaireDocument>(
      () => questionExample as unknown as PharmaBranchedQuestionnaireDocument,
    );

  const originalDocumentRef =
    useRef<PharmaBranchedQuestionnaireDocument>(workflowDocument);

  /**
   * Snapshot of every question's original `answer`, keyed by `question.name`.
   * Built once from {@link originalDocumentRef} on mount and never mutated —
   * used as the ground truth when we need to know what the user's original
   * answer was for a given question (e.g. to detect changes or revert).
   * Only questions with a non-null `name` are indexed.
   */
  const originalAnswersByNameRef = useRef<Map<string, Answer>>(
    buildOriginalAnswerMap(originalDocumentRef.current),
  );

  /**
   * Append-only log of question names whose `answer` has been patched, in the
   * order the user edited them. Updated inside {@link handlePatch} only for
   * answer-patches (free-text patches do not append). Never cleared
   * automatically — consumers that need a fresh slate should reset it
   * explicitly.
   */
  const updatedAnswerQuestionNamesRef = useRef<string[]>([]);

  const initialQuestions = useMemo<QuestionForUI[]>(
    () => transformCmmWorkflowToQuestionnaireForUI(workflowDocument).questions,
    [],
  );

  const [questions, setQuestions] = useState<QuestionForUI[]>(initialQuestions);

  const visibleQuestions = useMemo(
    () => getVisibleQuestionChain(questions),
    [questions],
  );

  const isQuestionnaireValid = useMemo(
    () =>
      validateQuestionnaireAnswers(
        visibleQuestions,
        originalAnswersByNameRef.current,
      ),
    [visibleQuestions],
  );

  useEffect(() => {
    console.log("validateQuestionnaireAnswers", isQuestionnaireValid);
  }, [isQuestionnaireValid]);

  const handlePatch = (patch: QuestionPatch) => {
    if ("answer" in patch) {
      const defaultAnswer = originalAnswersByNameRef.current.get(patch.name);
      const isAnswerChanged = defaultAnswer !== patch.answer;
      if (isAnswerChanged) {
        updatedAnswerQuestionNamesRef.current.push(patch.name);
      } else {
        updatedAnswerQuestionNamesRef.current =
          updatedAnswerQuestionNamesRef.current.filter(
            (name) => name !== patch.name,
          );
        setWorkflowDocument((prevDoc) => {
          return clearUserFeedbackFromCmmWorkflow(prevDoc, patch.name);
        });
      }
    }
    setWorkflowDocument((prevDoc) => {
      const { document: nextDoc, questions: nextQuestions } =
        applyPatchToCmmWorkflow(
          prevDoc,
          "answer" in patch
            ? { name: patch.name, answer: patch.answer }
            : {
                name: patch.name,
                choiceText: patch.free_text_answer.name,
                freeText: patch.free_text_answer.answer,
              },
        );
      setQuestions(nextQuestions);
      return nextDoc;
    });
  };

  const handleSubmit = (
    payload: AnalysisFeedbackPayload & { questionName: string },
  ) => {
    const userFeedback: UserFeedback = {
      selected_option: payload.selectedValues.join(","),
      comment: payload.comments,
      created_at: new Date().toISOString(),
      created_by: user?.email ?? "",
    };
    setWorkflowDocument((prevDoc) => {
      const nextDoc = appendUserFeedbackToCmmWorkflow(
        prevDoc,
        payload.questionName,
        userFeedback,
      );
      setQuestions(transformCmmWorkflowToQuestionnaireForUI(nextDoc).questions);
      return nextDoc;
    });
    updatedAnswerQuestionNamesRef.current =
      updatedAnswerQuestionNamesRef.current.filter(
        (name) => name !== payload.questionName,
      );
  };

  const handleCancel = (payload: { questionName: string }) => {
    const defaultAnswer = originalAnswersByNameRef.current.get(
      payload.questionName,
    );
    setWorkflowDocument((prevDoc) => {
      const { document: nextDoc, questions: nextQuestions } =
        applyPatchToCmmWorkflow(prevDoc, {
          name: payload.questionName,
          answer: defaultAnswer ?? "",
        });
      setQuestions(nextQuestions);
      return nextDoc;
    });
    updatedAnswerQuestionNamesRef.current =
      updatedAnswerQuestionNamesRef.current.filter(
        (name) => name !== payload.questionName,
      );
  };

  return (
    <div className="box-border flex h-full flex-col gap-4 overflow-y-auto bg-white p-4">
      {visibleQuestions.map((q, i) => {
        const common = {
          question: q,
          stepNumber: i + 1,
          onChange: handlePatch,
          evidenceCards: q.evidenceCards,
          confidenceScore: q.confidenceScore,
          answerChangedReason: q.answerChangedReason,
          shouldShowUserFeedBack:
            updatedAnswerQuestionNamesRef.current.includes(q.name),
          onSubmit: handleSubmit,
          onCancel: handleCancel,
        };
        if (q.question_type === "subjective") {
          return <SubjectiveQuestion key={q.name || i} {...common} />;
        }
        if (q.question_type === "radio-subjective") {
          return <RadioQuestion key={q.name || i} {...common} />;
        }
        return <ChecklistQuestion key={q.name || i} {...common} />;
      })}
    </div>
  );
};

export default TestQuestionnaire;
